'use client';
import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconStarFilled, IconStar } from '@repo/svgs';

export interface StarRatingProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Rating value (e.g. 4.2) */
  rating?: number;
  /** Maximum rating scale for the provided rating (default 5) */
  ratingScale?: number;
  /** Number of stars to display (default 5) */
  numberOfStars?: number;
  /** Whether the rating is static (non-interactive) */
  isStatic?: boolean;
  /** Size of each star in px (default 16) */
  starSize?: number;
  /** Space between stars in px (default 4) */
  starSpace?: number;
  /** Class applied to filled stars (default `icon-rating`) */
  fillClassName?: string;
  /** Class applied to empty stars (default `icon-tertiary`) */
  emptyClassName?: string;
  /** Use outline star icon for empty stars instead of filled with muted color */
  outlineEmpty?: boolean;
  /** Callback when rating changes (only for interactive mode) */
  onRatingChange?: (rating: number) => void;
}

function convertRating(rating: number, ratingScale: number, numberOfStars: number): number {
  if (ratingScale === numberOfStars) return rating;
  return (rating / ratingScale) * numberOfStars;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

export function StarRating({
  rating = 0,
  ratingScale = 5,
  numberOfStars = 5,
  isStatic = true,
  starSize = 16,
  starSpace = 4,
  fillClassName = 'icon-rating',
  emptyClassName = 'icon-tertiary',
  outlineEmpty = false,
  onRatingChange,
  className,
  ...rest
}: StarRatingProps) {
  const [hoverIndex, setHoverIndex] = useState<number>(-1);

  const normalizedRating = rating ?? 0;
  const convertedRating = useMemo(
    () => convertRating(normalizedRating, ratingScale, numberOfStars),
    [normalizedRating, ratingScale, numberOfStars],
  );

  const interactive = !isStatic && typeof onRatingChange === 'function';

  return (
    <div
      className={cn('flex items-center', className)}
      style={{ gap: starSpace }}
      {...rest}
      role={interactive ? 'group' : undefined}
      aria-label={interactive ? 'Star rating' : undefined}
    >
      {Array.from({ length: numberOfStars }).map((_, index) => {
        const starValue = index + 1;
        const displayRating = hoverIndex >= 0 ? hoverIndex + 1 : convertedRating;
        const fillPct = clamp(displayRating - index, 0, 1) * 100; // 0..100

        const star = (
          <span
            className="relative inline-block"
            style={{ width: starSize, height: starSize }}
            aria-hidden
          >
            {/* Empty */}
            {outlineEmpty ? (
              <IconStar
                className={cn(emptyClassName)}
                style={{ width: starSize, height: starSize }}
              />
            ) : (
              <IconStarFilled
                className={cn(emptyClassName)}
                style={{ width: starSize, height: starSize }}
              />
            )}
            {/* Filled overlay */}
            {fillPct > 0 ? (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                <IconStarFilled
                  className={cn(fillClassName)}
                  style={{ width: starSize, height: starSize }}
                />
              </span>
            ) : null}
          </span>
        );

        if (!interactive) {
          return (
            <span key={index} style={{ display: 'inline-flex' }}>
              {star}
            </span>
          );
        }

        return (
          <button
            key={index}
            type="button"
            className="p-0 bg-transparent border-0 inline-flex"
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(-1)}
            onFocus={() => setHoverIndex(index)}
            onBlur={() => setHoverIndex(-1)}
            onClick={() => onRatingChange(starValue)}
            aria-label={`Set rating to ${starValue}`}
          >
            {star}
          </button>
        );
      })}
    </div>
  );
}

export default StarRating;
